import { useEffect, useState, useCallback } from 'react'
import { Navigate, useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '../../layout/AppLayout.jsx'
import Card from '../../components/ui/Card.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Modal } from '../../components/ui/Modal.jsx'
import { Field, Textarea } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import api from '../../lib/api.js'
import { formatDate } from '../../lib/format.js'
import { downloadBlob } from '../../lib/download.js'
import { STEP_TONE } from './onboardingShared.js'

/*
 * Admin — review one onboarding dossier (inventory §4.2). Approve / reject /
 * message, with timeline, Stripe validation checklist and documents.
 */
export default function OnboardingDetails() {
  const { id } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isSuperAdmin } = useCompany()
  const { confirm, toast, notify } = useFeedback()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [dialog, setDialog] = useState(null) // 'reject' | 'message'
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [docView, setDocView] = useState(null) // { doc, url } for the preview modal

  const openDoc = async (doc) => {
    try {
      const { data: blob } = await api.get(`/admin/onboarding/${id}/document/${doc.id}`, { responseType: 'blob' })
      setDocView({ doc, url: URL.createObjectURL(blob) })
    } catch { toast(t('settings.errorGeneric'), 'error') }
  }
  const closeDoc = () => { if (docView?.url) URL.revokeObjectURL(docView.url); setDocView(null) }

  const load = useCallback(() => {
    setLoading(true)
    api.get(`/admin/onboarding/${id}`).then(({ data: d }) => setData(d)).catch(() => setNotFound(true)).finally(() => setLoading(false))
  }, [id])
  useEffect(() => { load() }, [load])

  if (!isSuperAdmin()) return <Navigate to="/app/dashboard" replace />
  if (loading) return <Page title={t('onboarding.detail.title')}><Card>{t('common.loading')}</Card></Page>
  if (notFound || !data) {
    return <Page title={t('onboarding.detail.title')}><Card style={{ textAlign: 'center', padding: 40 }}>
      <div style={{ marginBottom: 16, color: 'var(--color-text)' }}>{t('onboarding.detail.notFound')}</div>
      <Button variant="secondary" onClick={() => navigate('/app/admin/onboarding')}>{t('onboarding.detail.back')}</Button>
    </Card></Page>
  }

  const { onboarding, company, user, billing, documents = [], messages = [] } = data
  const canDecide = onboarding.current_step === 'pending_validation'

  const approve = async () => {
    const ok = await confirm({ tone: 'info', destructive: false, title: t('onboarding.detail.confirmApprove'), message: company.name, confirmLabel: t('onboarding.detail.approve'), cancelLabel: t('common.cancel') })
    if (!ok) return
    setBusy(true)
    try { await api.post(`/admin/onboarding/${id}/approve`, { notes: '' }); toast(t('onboarding.detail.approved'), 'success'); navigate('/app/admin/onboarding') }
    catch (e) { notify({ tone: 'error', title: t('admin.errorGeneric'), message: e?.response?.data?.error || '' }) } finally { setBusy(false) }
  }

  const submitDialog = async () => {
    setBusy(true)
    try {
      if (dialog === 'reject') { await api.post(`/admin/onboarding/${id}/reject`, { reason: text }); toast(t('onboarding.detail.rejected'), 'success'); navigate('/app/admin/onboarding') }
      else { await api.post(`/admin/onboarding/${id}/message`, { message: text }); toast(t('onboarding.detail.messageSent'), 'success'); setDialog(null); setText(''); load() }
    } catch (e) { notify({ tone: 'error', title: t('admin.errorGeneric'), message: e?.response?.data?.error || '' }) } finally { setBusy(false) }
  }

  const timeline = [
    ['registration_started_at', t('onboarding.timeline.started')],
    ['email_verified_at', t('onboarding.timeline.emailVerified')],
    ['terms_accepted_at', t('onboarding.timeline.terms')],
    ['documents_submitted_at', t('onboarding.timeline.documents')],
    ['validation_requested_at', t('onboarding.timeline.requested')],
  ]
  const checklist = [
    ['stripe_customer_id', 'Stripe customer'], ['stripe_payment_method_id', t('onboarding.checklist.paymentMethod')],
    ['stripe_setup_intent_id', 'Setup intent'], ['next_billing_date', t('onboarding.checklist.nextBilling')],
    ['billing_plan_name', t('onboarding.checklist.plan')],
  ]

  const action = (
    <Button variant="secondary" icon="invoice" onClick={() => navigate('/app/admin/onboarding')}>{t('onboarding.detail.back')}</Button>
  )

  return (
    <Page title={company.name} action={action} maxWidth={1000}>
      {/* Action bar */}
      <Card style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Badge tone={STEP_TONE[onboarding.current_step] || 'neutral'}>{t(`onboarding.status.${onboarding.current_step}`, onboarding.current_step)}</Badge>
        <div style={{ flex: 1 }} />
        <Button variant="secondary" icon="mail" onClick={() => { setDialog('message'); setText('') }}>{t('onboarding.detail.message')}</Button>
        {canDecide && <Button variant="danger" icon="x" onClick={() => { setDialog('reject'); setText('') }}>{t('onboarding.detail.reject')}</Button>}
        {canDecide && <Button variant="success" icon="check" loading={busy} onClick={approve}>{t('onboarding.detail.approve')}</Button>}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
        <Card>
          <SectionTitle>{t('onboarding.detail.company')}</SectionTitle>
          <Info label={t('admin.companies.vat')} value={company.vat_number} />
          <Info label={t('parties.fields.address')} value={`${company.address || ''} ${company.postal_code || ''} ${company.city || ''}`.trim()} />
          <Info label="Email" value={company.email} />
          <Info label={t('parties.fields.phone')} value={company.phone} />
        </Card>
        <Card>
          <SectionTitle>{t('onboarding.detail.representative')}</SectionTitle>
          <Info label={t('onboarding.list.representative')} value={[user.first_name, user.last_name].filter(Boolean).join(' ')} />
          <Info label="Email" value={user.email} />
          <Info label={t('onboarding.detail.emailVerified')} value={user.email_verified ? t('common.yes') : t('common.no')} />
          {onboarding.legal_representative_role && <Info label={t('onboarding.list.role')} value={onboarding.legal_representative_role} />}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
        <Card>
          <SectionTitle>{t('onboarding.detail.timeline')}</SectionTitle>
          {timeline.map(([k, label]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
              <span style={{ color: onboarding[k] ? 'var(--color-success)' : 'var(--color-text-muted)' }}><Icon name={onboarding[k] ? 'check' : 'x'} size={16} /></span>
              <span style={{ flex: 1, fontSize: 13.5, color: 'var(--color-text)' }}>{label}</span>
              <span style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{onboarding[k] ? formatDate(onboarding[k]) : '—'}</span>
            </div>
          ))}
        </Card>
        <Card>
          <SectionTitle>{t('onboarding.detail.checklist')}</SectionTitle>
          {checklist.map(([k, label]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
              <span style={{ flexShrink: 0, color: billing?.[k] ? 'var(--color-success)' : 'var(--color-text-muted)' }}><Icon name={billing?.[k] ? 'check' : 'x'} size={16} /></span>
              <span style={{ flexShrink: 0, fontSize: 13.5, color: 'var(--color-text)' }}>{label}</span>
              <span title={billing?.[k] || ''} style={{ flex: 1, textAlign: 'right', fontSize: 12.5, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{billing?.[k] || '—'}</span>
            </div>
          ))}
        </Card>
      </div>

      <Card style={{ marginBottom: 18 }}>
        <SectionTitle>{t('onboarding.detail.documents')}</SectionTitle>
        {documents.length === 0 ? <div style={{ color: 'var(--color-text-muted)', fontSize: 13.5 }}>{t('onboarding.detail.noDocs')}</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {documents.map((doc) => (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 10 }}>
                <Icon name="invoice" size={18} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: 'var(--color-ink)', fontSize: 13.5 }}>{doc.filename}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{doc.type} · {Math.round((doc.file_size || 0) / 1024)} Ko</div>
                </div>
                <Button variant="secondary" icon="eye" onClick={() => openDoc(doc)}>{t('common.preview')}</Button>
                <Button variant="secondary" icon="download" onClick={() => downloadBlob(`/admin/onboarding/${id}/document/${doc.id}`, doc.filename)}>{t('common.download')}</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <SectionTitle>{t('onboarding.detail.messages')}</SectionTitle>
        {messages.length === 0 ? <div style={{ color: 'var(--color-text-muted)', fontSize: 13.5 }}>{t('onboarding.detail.noMessages')}</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.map((m, i) => (
              <div key={m.id ?? i} style={{ padding: '10px 12px', background: 'var(--color-bg-lilac)', borderRadius: 10 }}>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 3 }}>{m.author || m.sender || '—'} · {m.created_at ? formatDate(m.created_at) : ''}</div>
                <div style={{ fontSize: 13.5, color: 'var(--color-ink)' }}>{m.message || m.content}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal open={!!dialog} onClose={() => setDialog(null)} maxWidth={480} closeOnBackdrop={false}>
        <div style={{ padding: 24 }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, marginBottom: 14 }}>{dialog === 'reject' ? t('onboarding.detail.rejectTitle') : t('onboarding.detail.messageTitle')}</h3>
          <Field label={dialog === 'reject' ? t('onboarding.detail.reason') : t('onboarding.detail.yourMessage')}>
            <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button variant="secondary" onClick={() => setDialog(null)}>{t('common.cancel')}</Button>
            <Button variant={dialog === 'reject' ? 'danger' : 'primary'} icon={dialog === 'reject' ? 'x' : 'send'} loading={busy} disabled={!text.trim()} onClick={submitDialog}>{dialog === 'reject' ? t('onboarding.detail.reject') : t('onboarding.detail.send')}</Button>
          </div>
        </div>
      </Modal>

      {docView && <DocViewerModal t={t} doc={docView.doc} url={docView.url} onClose={closeDoc} onDownload={() => downloadBlob(`/admin/onboarding/${id}/document/${docView.doc.id}`, docView.doc.filename)} />}
    </Page>
  )
}

function SectionTitle({ children }) {
  return <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 14, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 12 }}>{children}</h3>
}
function Info({ label, value }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--color-ink)' }}>{value || '—'}</div>
    </div>
  )
}

function DocViewerModal({ t, doc, url, onClose, onDownload }) {
  const name = (doc.filename || '').toLowerCase()
  const mime = (doc.mime_type || '').toLowerCase()
  const isPdf = mime.includes('pdf') || name.endsWith('.pdf')
  const isImg = mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)
  return (
    <Modal open onClose={onClose} maxWidth={920}>
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '88vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--color-border-row)' }}>
          <Icon name="invoice" size={18} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--color-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.filename}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{doc.type}</div>
          </div>
          <Button variant="secondary" icon="download" onClick={onDownload}>{t('common.download')}</Button>
          <Button variant="secondary" icon="x" onClick={onClose}>{t('common.close')}</Button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', background: 'var(--color-bg-page)', padding: isImg ? 16 : 0, minHeight: 360 }}>
          {isImg ? (
            <img src={url} alt={doc.filename} style={{ maxWidth: '100%', display: 'block', margin: '0 auto', borderRadius: 8 }} />
          ) : isPdf ? (
            <iframe title={doc.filename} src={url} style={{ width: '100%', height: '74vh', border: 'none', display: 'block' }} />
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>
              {t('onboarding.detail.noPreview')}<br />
              <Button variant="primary" icon="download" onClick={onDownload} style={{ marginTop: 14 }}>{t('common.download')}</Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
